import { makeMartyPixelQueryString } from 'helpers/ErrorUtils';
import {
  CLEAR_POST_RETURN_INFO,
  CLEAR_RETURN_ERROR,
  CLEAR_RETURN_STATES,
  CLEAR_SUBMIT_RETURN_ERROR,
  INITIATE_RETURN_BEGIN,
  RECEIVE_CONTRACT_RETURN_INFO,
  RECEIVE_MULTIPLE_PRE_RETURN_INFO,
  RECEIVE_POST_RETURN_INFO,
  RECEIVE_PRE_RETURN_INFO,
  RECEIVE_RETURN_LABEL_INFO,
  RECEIVE_RETURN_LABEL_INFO_ERROR,
  RECEIVE_RETURN_LABEL_INFO_START,
  RECEIVE_RETURN_LABEL_PRODUCT_INFO,
  RESET_RETURNS_IN_STATE,
  RETURN_CANCELLATION_CONFIRMED,
  RETURN_CANCELLATION_ERROR,
  RETURN_CANCELLATION_REQUESTED,
  RETURN_PREVENTING_SUBMIT_ERROR,
  RETURN_SUBMIT_COMPLETE,
  RETURN_SUBMIT_ERROR,
  RETURN_SUBMIT_INITIATE,
  SET_RETURN_ERROR,
  STEP_SELECTION,
  SUBMIT_RETURN_ERROR,
  UPDATE_BOX_INFO
} from 'constants/reduxActions';
import { EMPTY_REASON } from 'constants/returnReasons';
import { productBundle } from 'apis/cloudcatalog';
import { recordMyAccountError } from 'apis/account';
import { getPrereturnInfo, placeGuestReturn, placeReturn, returnLabelInfo, setRefundOnFirstScanPreference } from 'apis/mafia';
import { prependAppRoot } from 'history/AppRootUtils';
import { authenticationErrorCatchHandlerFactory, err, setError } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import timedFetch from 'middleware/timedFetch';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchErrorMiddlewareAllowedErrors,
  fetchMixedReturnErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { setFederatedLoginModalVisibility, setHeaderFooterVisibility } from 'actions/headerfooter';
import marketplace from 'cfg/marketplace.json';
import { selectMafiaConfig } from 'selectors/environment';
import { trackOperation, trackOperationFailureIfAuthed } from 'events/ops/opsEvent';

const { authUrl, hasFederatedLogin } = marketplace;

const AUTH_ERROR = 'AUTHENTICATION_ERROR';

export function receivePrereturnInfo(payload) {
  return {
    type: RECEIVE_PRE_RETURN_INFO,
    preReturnInfo: payload
  };
}

export function receiveMultiplePrereturnInfo(payload) {
  return {
    type: RECEIVE_MULTIPLE_PRE_RETURN_INFO,
    multiPreReturnInfo: payload
  };
}

export function initiateReturnBegin() {
  return {
    type: INITIATE_RETURN_BEGIN
  };
}

export function receivePostReturnInfo(payload) {
  return {
    type: RECEIVE_POST_RETURN_INFO,
    postReturnInfo: payload
  };
}

export function stepSelection(step) {
  return {
    type: STEP_SELECTION,
    step
  };
}

export function returnSubmitInitiate() {
  return {
    type: RETURN_SUBMIT_INITIATE
  };
}

export function returnSubmitComplete() {
  return {
    type: RETURN_SUBMIT_COMPLETE
  };
}

export function clearReturnStates() {
  return {
    type: CLEAR_RETURN_STATES
  };
}

export function clearPostReturnInfo() {
  return {
    type: CLEAR_POST_RETURN_INFO
  };
}

export function receiveReturnLabelInfo(labelInfo) {
  return {
    type: RECEIVE_RETURN_LABEL_INFO,
    labelInfo,
    isReturnLabelLoading: false
  };
}
export function receiveReturnLabelInfoStart() {
  return {
    type: RECEIVE_RETURN_LABEL_INFO_START
  };
}

export function receiveReturnLabelInfoError() {
  return {
    type: RECEIVE_RETURN_LABEL_INFO_ERROR
  };
}

export function receiveContractReturnInfo(payload) {
  return {
    type: RECEIVE_CONTRACT_RETURN_INFO,
    contractReturnInfo: payload
  };
}

export function receiveReturnLabelProductInfo(productInfo) {
  return {
    type: RECEIVE_RETURN_LABEL_PRODUCT_INFO,
    productInfo
  };
}

export function returnPreventingSubmitError() {
  return {
    type: RETURN_PREVENTING_SUBMIT_ERROR
  };
}

export function returnSubmitError(errorDetails) {
  recordMyAccountError(makeMartyPixelQueryString({ type: 'martyMyAccountError', subType: 'returns' }, errorDetails));
  return {
    type: RETURN_SUBMIT_ERROR
  };
}

export function setReturnErrorType(returnErrorType) {
  return {
    type: SET_RETURN_ERROR,
    returnErrorType
  };
}

export function clearReturnError() {
  return {
    type: CLEAR_RETURN_ERROR
  };
}

export function resetReturnsInState() {
  return {
    type: RESET_RETURNS_IN_STATE
  };
}

export function returnCancellationRequest() {
  return {
    type: RETURN_CANCELLATION_REQUESTED
  };
}

export function returnCancellationConfirmed() {
  return {
    type: RETURN_CANCELLATION_CONFIRMED
  };
}

export function returnCancellationError() {
  return {
    type: RETURN_CANCELLATION_ERROR
  };
}

export function submitReturnError() {
  return {
    type: SUBMIT_RETURN_ERROR
  };
}
export function clearSubmitReturnError() {
  return {
    type: CLEAR_SUBMIT_RETURN_ERROR
  };
}

// Updating the total number of boxes to be used for return
export function updateBoxNumInfo(numOfBoxes, boxInfo = {}, items) {
  const currentBoxesInStore = Object.keys(boxInfo).length;

  if (numOfBoxes === 1 || numOfBoxes === 0 || currentBoxesInStore === 0) {
    // reset box sorting; move all returnable items back to Box #1
    // need items to be deep copy otherwise original items array gets mutated after mapPrereturnInfoToBox :/
    boxInfo = { 1: { items: [...items] } };
  } else if (numOfBoxes > currentBoxesInStore) {
    // add extra boxes to boxInfo
    for (let i = currentBoxesInStore + 1; i <= numOfBoxes; i++) {
      boxInfo[i] = { items: [] }; // add and initialize more boxes
    }
  } else {
    // since not adding or initializing must be reducing boxes, move removed boxes items to box 1, then delete the box
    for (let i = numOfBoxes + 1; i <= currentBoxesInStore; i++) {
      if (boxInfo[i]) {
        boxInfo[1].items = boxInfo[1].items.concat(boxInfo[i].items);
        delete boxInfo[i];
      }
    }
  }

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

// Selecting which items go into which box
export function updateItemInBoxInfo(boxNum, itemId, boxInfo) {
  let itemPlacedInBox = {};
  // loop through boxes in state and remove item from existing box.
  for (const box in boxInfo) {
    const { items } = boxInfo[box];
    const newItemsArr = [];

    items.forEach(item => {
      const { itemUniqueId: id } = item;
      if (id === itemId) {
        itemPlacedInBox = item; // save item
      } else {
        newItemsArr.push(item);
      }
    });
    boxInfo[box].items = newItemsArr;
  }
  boxInfo[boxNum].items.push(itemPlacedInBox); // place item in newly selected box

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

// Map response from return/initiate to box object in state
export function mapPrereturnInfoToBox(preReturnInfo, boxInfo) {
  const alreadyUsed = {};
  for (const box in boxInfo) {
    const { items } = boxInfo[box];

    items.forEach((item, i) => {
      if (item?.product) {
        const {
          product: { asin: itemAsin }
        } = item;

        preReturnInfo.forEach(order => {
          const { returnItems } = order;
          for (let j = 0; j < returnItems.length; j++) {
            const returnItem = returnItems[j];
            const { asin: boxItemAsin, unitId } = returnItem;
            const shouldReplaceItemInStoreWithPreReturnInfoItem = itemAsin === boxItemAsin && !alreadyUsed[unitId];
            if (shouldReplaceItemInStoreWithPreReturnInfoItem) {
              items[i] = returnItem;
              items[i].reasonCode = EMPTY_REASON.reasonCode;
              alreadyUsed[unitId] = true;
              break;
            }
          }
        });
      }
    });
  }

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

export function updateBoxInfoWithReturnReason(unitId, reasonCode, boxInfo, boxNum) {
  const { items } = boxInfo[boxNum];

  items.forEach(item => {
    const { unitId: itemUnitId } = item;
    if (itemUnitId === unitId) {
      item.reasonCode = reasonCode;
    }
  });

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

export function fetchSpecificReturnInfo(orderId, orderAsins, fetchPrereturnInfoEndpoint = getPrereturnInfo) {
  return (dispatch, getState) => {
    dispatch(setReturnErrorType(null));
    dispatch(initiateReturnBegin());

    const state = getState();
    const {
      cookies,
      router: { location },
      returns: { boxInfo }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return fetchPrereturnInfoEndpoint(mafiaConfig, orderId, orderAsins, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        if (Array.isArray(orderId)) {
          dispatch(receiveMultiplePrereturnInfo(resp));
        } else {
          // Mafia returns null value for orderId on prereturn info so need to map it back in
          resp.returnItems.forEach(item => {
            item.orderId = item.orderId || orderId;
          });
          dispatch(receivePrereturnInfo(resp));
        }
        const {
          returns: { preReturnInfo }
        } = getState();
        dispatch(mapPrereturnInfoToBox(preReturnInfo, boxInfo));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/returns', location)));
  };
}

export function fetchAdditionalContractId(orderId, orderItemId, fetchPrereturnInfoEndpoint = getPrereturnInfo) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return fetchPrereturnInfoEndpoint(mafiaConfig, orderId, orderItemId, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ contractId }) => contractId)
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/returns', location)));
  };
}

export function submitReturn(returnInfo, placeAuthReturn = placeReturn) {
  return function (dispatch, getState) {
    const state = getState();
    const { authentication, cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    const submitReturnEndpoint = authentication ? placeAuthReturn : placeGuestReturn;

    return submitReturnEndpoint(mafiaConfig, returnInfo, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMixedReturnErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receivePostReturnInfo(response));
        trackOperation('ReturnSubmit', 'success');
      })
      .catch(err => {
        dispatch(submitReturnError());
        trackOperationFailureIfAuthed('ReturnSubmit', err?.status);
        if (err.status === 400 || err.status === 500) {
          dispatch(setReturnErrorType(err.id));
          throw err;
        } else {
          authenticationErrorCatchHandlerFactory(dispatch, '/return')(err);
        }
      });
  };
}

export function updateRefundOnFirstScanPreferences(rofsPreference, setRefundOnFirstScanPreferenceApi = setRefundOnFirstScanPreference) {
  return function (dispatch, getState) {
    const state = getState();
    const {
      cookies,
      environmentConfig: { akitaKey }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return setRefundOnFirstScanPreferenceApi(mafiaConfig, akitaKey, cookies, rofsPreference)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware);
  };
}

export function fetchReturnLabelInfo(
  { rmaId, contractId, isReturnFetchOnlyEndpoint },
  getReturnLabelInfo = returnLabelInfo,
  getProductBundle = productBundle,
  queryParamCredentials = {},
  labelImageUrlHandler = url => url,
  postAuthReturnTo,
  customerLogout
) {
  return function (dispatch, getState) {
    if (!(rmaId || contractId)) {
      return dispatch(setError('Error loading return label-- no RMA or Contract ID.', null, 404));
    } else {
      const state = getState();
      const {
        cookies,
        environmentConfig: {
          api: { cloudcatalog }
        }
      } = state;
      const mafiaConfig = selectMafiaConfig(state);

      dispatch(receiveReturnLabelInfoStart());

      return getReturnLabelInfo(mafiaConfig, { rmaId, contractId, isReturnFetchOnlyEndpoint }, cookies, queryParamCredentials)
        .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
        .then(fetchErrorMiddlewareAllowedErrors([401, 403]))
        .then(requireAuthentication(dispatch, postAuthReturnTo, customerLogout))
        .then(async response => {
          const { contractSummary } = response;
          if (contractSummary?.length) {
            dispatch(receiveContractReturnInfo(contractSummary[0]));
          } else {
            const { labelImageUrl } = response;
            response.labelImageUrl = await labelImageUrlHandler(labelImageUrl, cookies, queryParamCredentials);
            dispatch(receiveReturnLabelInfo(response));

            const { asins } = response;
            if (asins?.length) {
              asins.forEach(asin =>
                getProductBundle(cloudcatalog, { asin }, timedFetch('cloudCatalogProduct'))
                  .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
                  .then(response => response.json())
                  .then(response => {
                    if (response.product?.length === 1) {
                      const product = { ...response.product[0], asin };
                      dispatch(receiveReturnLabelProductInfo(product));
                    }
                  })
              );
            }
          }
        })
        .catch(e => {
          if (e.name !== AUTH_ERROR) {
            receiveReturnLabelInfoError();
            dispatch(setError(err.GENERIC, e));
          }
        });
    }
  };
}

const requireAuthentication = (dispatch, returnTo, customerLogout) => async response => {
  if (response.statusCode !== 401 && response.statusCode !== 403) {
    return response;
  }

  await customerLogout();
  if (hasFederatedLogin) {
    dispatch(setHeaderFooterVisibility(true));
    dispatch(
      setFederatedLoginModalVisibility(true, {
        returnTo,
        redirectOnClose: '/'
      })
    );
  } else {
    window.location.href = `${authUrl}${returnTo}`;
  }

  const authError = new Error();
  authError.name = AUTH_ERROR;
  throw authError;
};

export default {
  receivePrereturnInfo,
  receivePostReturnInfo,
  stepSelection,
  returnSubmitInitiate,
  returnSubmitComplete,
  receiveReturnLabelInfo,
  receiveContractReturnInfo,
  receiveReturnLabelProductInfo,
  returnPreventingSubmitError,
  returnSubmitError,
  setReturnErrorType,
  resetReturnsInState,
  updateBoxNumInfo,
  updateItemInBoxInfo,
  mapPrereturnInfoToBox,
  updateBoxInfoWithReturnReason,
  fetchSpecificReturnInfo,
  fetchAdditionalContractId,
  clearReturnError,
  submitReturn,
  updateRefundOnFirstScanPreferences,
  fetchReturnLabelInfo,
  clearReturnStates,
  clearPostReturnInfo,
  submitReturnError,
  clearSubmitReturnError
};
